<template>
  <el-dialog
    title="Bulk Confirm Results"
    width="80%"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    class="bulk-confirm-result-dialog">

    <!-- 结果摘要 -->
    <div class="result-summary">
      <el-alert
        :title="summaryTitle"
        :type="summaryType"
        :closable="false"
        show-icon>
        <div slot="description">
          <p><strong>Total Packages:</strong> {{ result.totalPackages }}</p>
          <p><strong>Successful:</strong> {{ result.successCount }}</p>
          <p><strong>Failed:</strong> {{ result.failedCount }}</p>
          <p v-if="result.batchNumber"><strong>Batch Number:</strong> {{ result.batchNumber }}</p>
        </div>
      </el-alert>
    </div>

    <!-- 成功记录摘要 -->
    <div v-if="result.successCount > 0" class="success-section">
      <h3 class="section-title success-title">
        <i class="el-icon-circle-check"></i>
        Successfully Created Packages: {{ result.successCount }}
      </h3>
    </div>

    <!-- 失败记录 -->
    <div v-if="result.failedCount > 0" class="failure-section">
      <div class="failure-header">
        <h3 class="section-title error-title">
          <i class="el-icon-circle-close"></i>
          Failed Packages ({{ result.failedCount }})
        </h3>
        <div class="failure-actions">
          <el-button
            size="small"
            icon="el-icon-document-copy"
            @click="copyFailedData">
            Copy to Clipboard
          </el-button>
          <el-button
            size="small"
            type="primary"
            icon="el-icon-download"
            @click="exportFailedData">
            Export to CSV
          </el-button>
        </div>
      </div>

      <el-table
        :data="result.failureRecords"
        style="width: 100%;"
        max-height="400">
        <el-table-column prop="tracking" label="Tracking Number" min-width="180">
          <template slot-scope="scope">
            <code class="tracking-code">{{ scope.row.tracking }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="itemCount" label="UPC Items" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="mini">{{ scope.row.itemCount || 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="errorMessage" label="Error Reason" min-width="400">
          <template slot-scope="scope">
            <div class="error-message-cell">
              <el-tooltip :content="scope.row.errorMessage" placement="top">
                <span class="error-text">{{ scope.row.errorMessage }}</span>
              </el-tooltip>
              <el-tag
                :type="scope.row.canRetry ? 'warning' : 'danger'"
                size="mini"
                class="retry-tag">
                {{ scope.row.canRetry ? 'Can Retry' : 'Cannot Retry' }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 操作统计 -->
    <div v-if="result.failedCount > 0" class="failure-statistics">
      <h4>Failure Analysis</h4>
      <div class="stats-grid">
        <div class="stat-item">
          <span class="stat-label">Duplicate Tracking:</span>
          <span class="stat-value duplicate">{{ duplicateCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Retryable Errors:</span>
          <span class="stat-value retryable">{{ retryableCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Non-retryable Errors:</span>
          <span class="stat-value non-retryable">{{ nonRetryableCount }}</span>
        </div>
      </div>
    </div>

    <!-- 对话框底部 -->
    <div slot="footer" class="dialog-footer">
      <div class="footer-left">
        <el-button
          v-if="hasRetryableFailures"
          type="warning"
          @click="retryFailures"
          :loading="retrying">
          <i class="el-icon-refresh"></i>
          Retry Failed Packages ({{ retryableCount }})
        </el-button>
      </div>
      <div class="footer-right">
        <el-button @click="closeDialog">Close</el-button>
        <el-button
          v-if="result.successCount > 0"
          type="primary"
          @click="continueOperation">
          <i class="el-icon-check"></i>
          Continue
        </el-button>
      </div>
    </div>

  </el-dialog>
</template>

<script>
export default {
  name: 'BulkConfirmResultDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    result: {
      type: Object,
      default: () => ({
        totalPackages: 0,
        successCount: 0,
        failedCount: 0,
        createdPackageIds: [],
        failureRecords: [],
        batchNumber: ''
      })
    }
  },
  data() {
    return {
      retrying: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },
    summaryTitle() {
      if (this.result.failedCount === 0) {
        return 'All Packages Created Successfully!'
      } else if (this.result.successCount === 0) {
        return 'All Packages Failed to Create'
      } else {
        return 'Bulk Confirm Completed with Mixed Results'
      }
    },
    summaryType() {
      if (this.result.failedCount === 0) {
        return 'success'
      } else if (this.result.successCount === 0) {
        return 'error'
      } else {
        return 'warning'
      }
    },
    duplicateCount() {
      return this.result.failureRecords.filter(record =>
        this.isDuplicateTracking(record)
      ).length
    },
    retryableCount() {
      return this.result.failureRecords.filter(record =>
        record.canRetry
      ).length
    },
    nonRetryableCount() {
      return this.result.failureRecords.filter(record =>
        !record.canRetry
      ).length
    },
    hasRetryableFailures() {
      return this.retryableCount > 0
    }
  },
  methods: {
    formatPackageId(packageId) {
      // 如果是JSON对象，尝试提取ID
      if (typeof packageId === 'object') {
        return packageId.mixedPackageId || packageId.packageId || packageId.id || JSON.stringify(packageId)
      }
      // 如果是字符串，直接返回
      return packageId
    },
    isDuplicateTracking(record) {
      if (!record.errorMessage) return false

      const errorMsg = record.errorMessage.toLowerCase()
      return errorMsg.includes('快递单号已存在') ||
             errorMsg.includes('tracking number already exists') ||
             errorMsg.includes('duplicate tracking') ||
             errorMsg.includes('tracking号重复') ||
             errorMsg.includes('duplicate entry') && errorMsg.includes('uk_tracking')
    },
    retryFailures() {
      const retryableRecords = this.result.failureRecords.filter(record => record.canRetry)

      if (retryableRecords.length === 0) {
        this.$message.warning('No retryable failures found')
        return
      }

      // 构建重试数据 - 只包含可重试的记录
      const retryData = {
        failedRecords: retryableRecords,
        batchNumber: this.result.batchNumber
      }

      this.$emit('retry-failed', retryData)
    },
    continueOperation() {
      this.$emit('continue', this.result)
      this.closeDialog()
    },
    closeDialog() {
      this.dialogVisible = false
      this.$emit('close')
    },

    // 复制失败的Tracking Number到剪贴板
    copyFailedData() {
      if (!this.result.failureRecords || this.result.failureRecords.length === 0) {
        this.$message.warning('No failed tracking numbers to copy')
        return
      }

      try {
        // 提取所有失败的tracking number
        const failedTrackingNumbers = this.result.failureRecords
          .map(record => record.tracking)
          .filter(tracking => tracking) // 过滤空值
          .join('\n') // 每行一个tracking number

        // 复制到剪贴板
        navigator.clipboard.writeText(failedTrackingNumbers).then(() => {
          this.$message.success(`${this.result.failureRecords.length} failed tracking numbers copied to clipboard`)
        }).catch(err => {
          // 降级方案：使用document.execCommand
          this.fallbackCopyToClipboard(failedTrackingNumbers)
        })
      } catch (error) {
        this.$message.error('Failed to copy tracking numbers to clipboard')
      }
    },

    // 降级复制方案
    fallbackCopyToClipboard(text) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        const successful = document.execCommand('copy')
        if (successful) {
          this.$message.success(`${this.result.failureRecords.length} failed records copied to clipboard`)
        } else {
          this.$message.error('Failed to copy data to clipboard')
        }
      } catch (err) {
        this.$message.error('Failed to copy data to clipboard')
      } finally {
        document.body.removeChild(textArea)
      }
    },

    // 导出失败数据为CSV
    exportFailedData() {
      if (!this.result.failureRecords || this.result.failureRecords.length === 0) {
        this.$message.warning('No failed data to export')
        return
      }

      try {
        // 构建CSV内容
        const headers = ['Tracking Number', 'UPC Items', 'Error Reason', 'Can Retry', 'Batch Number', 'Export Time']
        const csvRows = []

        // 添加表头
        csvRows.push(headers.map(header => `"${header}"`).join(','))

        // 添加数据行
        const exportTime = new Date().toISOString().replace('T', ' ').substr(0, 19)
        this.result.failureRecords.forEach(record => {
          const row = [
            record.tracking || '',
            record.itemCount || 0,
            (record.errorMessage || 'Unknown error').replace(/"/g, '""'), // 转义双引号
            record.canRetry ? 'Yes' : 'No',
            this.result.batchNumber || '',
            exportTime
          ]
          csvRows.push(row.map(cell => `"${cell}"`).join(','))
        })

        const csvContent = csvRows.join('\n')
        const BOM = '\uFEFF' // 添加BOM以支持中文

        // 创建下载链接
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')

        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob)
          link.setAttribute('href', url)

          // 生成文件名
          const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')
          const batchPart = this.result.batchNumber ? `_${this.result.batchNumber}` : ''
          const filename = `bulk_confirm_failed_packages${batchPart}_${timestamp}.csv`

          link.setAttribute('download', filename)
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)

          this.$message.success(`Failed packages exported to ${filename}`)
        } else {
          this.$message.error('Export is not supported by this browser')
        }
      } catch (error) {
        this.$message.error('Failed to export data')
      }
    }
  }
}
</script>

<style scoped>
.bulk-confirm-result-dialog {
  .el-dialog {
    border-radius: 8px;
  }

  .el-dialog__body {
    padding: 24px;
  }
}

.result-summary {
  margin-bottom: 24px;
}

.result-summary .el-alert__description p {
  margin: 4px 0;
  font-size: 14px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 20px 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.success-title {
  color: #67c23a;
}

.error-title {
  color: #f56c6c;
}

.success-section {
  margin-bottom: 24px;
}

.success-section .section-title {
  color: #67c23a;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.failure-section {
  margin-bottom: 24px;
}

.failure-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.failure-header .section-title {
  margin: 0;
  flex: 1;
  min-width: 200px;
}

.failure-actions {
  display: flex;
  gap: 8px;
}

.tracking-code {
  font-family: 'Monaco', 'Consolas', monospace;
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  color: #606266;
}

.error-message-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.error-text {
  font-size: 13px;
  color: #f56c6c;
  line-height: 1.4;
}

.retry-tag {
  align-self: flex-start;
}


.failure-statistics {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 24px;
}

.failure-statistics h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.stat-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.stat-value {
  font-size: 14px;
  font-weight: 600;
}

.stat-value.duplicate {
  color: #e6a23c;
}

.stat-value.retryable {
  color: #409eff;
}

.stat-value.non-retryable {
  color: #f56c6c;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 0 0;
  border-top: 1px solid #e4e7ed;
}

.footer-left,
.footer-right {
  display: flex;
  gap: 12px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .bulk-confirm-result-dialog .el-dialog {
    width: 95% !important;
    margin: 20px auto;
  }

  .failure-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .failure-header .section-title {
    min-width: auto;
    margin-bottom: 8px;
  }

  .failure-actions {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .dialog-footer {
    flex-direction: column;
    gap: 12px;
  }

  .footer-left,
  .footer-right {
    width: 100%;
    justify-content: center;
  }
}
</style>


// WEBPACK FOOTER //
// BulkConfirmResultDialog.vue?22b87e7e